import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';

import { EmptyStateComponent } from '../../../empty-state/empty-state';
import type {
  DpiaMeasure,
  DpiaRisk,
  DpiaRiskProfileData,
  DpiaSignOff,
} from '../../../../models/assessments/dpia.model';
import { DpiaOverallRisk } from '../../../../constants/assessment.constants';
import { getDpiaSignOffDecisionVariant } from '../../../../utils/status-indicator.utils';
import { analyseDpiaMeasures } from '../shared/link-hydrator';
import { DpiaRiskBadgeComponent } from '../risk-badge/risk-badge';
import type { StatusVariant } from '../../../status-indicator/status-indicator';

type SignOffRole = DpiaSignOff['role'];
const ROLES: readonly SignOffRole[] = ['DPO', 'MANAGEMENT', 'OTHER'];

interface RiskRow {
  id: string;
  title: string;
  likelihoodKey: string;
  severityKey: string;
  overall: DpiaOverallRisk | null;
  residual: DpiaOverallRisk | null;
  measuresCount: number;
  hasUnresolvedLinks: boolean;
}

interface SignOffSlotRow {
  role: SignOffRole;
  roleLabelKey: string;
  signed: DpiaSignOff | null;
  decisionVariant: StatusVariant | null;
  decisionLabelKey: string | null;
}

/** DPIA drawer Report tab — the DPIA record's substantive content.
 *  Order: risk snapshot → prior-consultation callout → risks list →
 *  sign-off ledger. Read-only preview surface. Editing / signing lives
 *  on the full page. */
@Component({
  selector: 'app-dpia-drawer-report',
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslocoModule,
    EmptyStateComponent,
    DpiaRiskBadgeComponent,
  ],
  templateUrl: './drawer-report.html',
  styleUrl: './drawer-report.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DpiaDrawerReportComponent {
  readonly dpia = input<DpiaRiskProfileData | null>(null);

  // ── Risk snapshot ─────────────────────────────────────────────
  // Template pipes the raw enum values straight into
  // `<app-dpia-risk-badge>`, so we only need to surface the raw values
  // as computeds — tonal-class + i18n-key derivation lives inside the
  // badge component (single source of truth).
  protected readonly overallRisk = computed(() => this.dpia()?.overall_risk ?? null);
  protected readonly residualRisk = computed(() => this.dpia()?.residual_overall_risk ?? null);
  protected readonly hasRiskData = computed(() => this.overallRisk() !== null);

  protected readonly needsPriorConsultation = computed(() => {
    const p = this.dpia();
    return (
      !!p && (p.prior_consultation_required || p.residual_overall_risk === DpiaOverallRisk.HIGH)
    );
  });

  // ── Risks list ────────────────────────────────────────────────
  private readonly analysis = computed(() => analyseDpiaMeasures(this.dpia()));

  private readonly unresolvedMeasureIds = computed<Set<string>>(() => {
    const a = this.analysis();
    return new Set<string>([...a.unresolvedControlIds, ...a.unresolvedTaskIds]);
  });

  private readonly measuresByRisk = computed<Map<string, DpiaMeasure[]>>(() => {
    const map = new Map<string, DpiaMeasure[]>();
    for (const m of this.dpia()?.measures ?? []) {
      const arr = map.get(m.risk_id) ?? [];
      arr.push(m);
      map.set(m.risk_id, arr);
    }
    return map;
  });

  protected readonly riskRows = computed<readonly RiskRow[]>(() => {
    const risks: DpiaRisk[] = this.dpia()?.risks ?? [];
    const unresolved = this.unresolvedMeasureIds();
    const byRisk = this.measuresByRisk();
    return risks.map(risk => {
      const measures = byRisk.get(risk.id) ?? [];
      return {
        id: risk.id,
        title: risk.title,
        likelihoodKey: `dpia.matrix.likelihood_${risk.likelihood.toLowerCase()}`,
        severityKey: `dpia.matrix.severity_${risk.severity.toLowerCase()}`,
        overall: risk.overall ?? null,
        residual: risk.residual_overall ?? null,
        measuresCount: measures.length,
        hasUnresolvedLinks: measures.some(m => unresolved.has(m.measure_id)),
      };
    });
  });

  protected readonly hasRisks = computed(() => this.riskRows().length > 0);

  // ── Sign-off ledger ───────────────────────────────────────────
  protected readonly signOffs = computed<DpiaSignOff[]>(() => this.dpia()?.sign_offs ?? []);

  protected readonly signOffProgress = computed(() => ({
    signed: ROLES.filter(role => this.signOffs().some(s => s.role === role)).length,
    total: ROLES.length,
  }));

  protected readonly signOffSlots = computed<readonly SignOffSlotRow[]>(() => {
    const byRole = new Map(this.signOffs().map(s => [s.role, s]));
    return ROLES.map(role => {
      const signed = byRole.get(role) ?? null;
      return {
        role,
        roleLabelKey: `dpia.sign_off.role_${role.toLowerCase()}`,
        signed,
        decisionVariant: signed ? getDpiaSignOffDecisionVariant(signed.decision) : null,
        decisionLabelKey: signed ? `dpia.sign_off.decision_${signed.decision.toLowerCase()}` : null,
      };
    });
  });

  // ── Overall empty state ───────────────────────────────────────
  protected readonly isEmpty = computed(
    () => !this.hasRiskData() && !this.hasRisks() && this.signOffs().length === 0
  );
}
